import React, { useState, useEffect, useCallback } from 'react';
import ConfettiAnimation from '../shared/ConfettiAnimation';

interface Card {
  id: number;
  emoji: string;
  isFlipped: boolean;
  isMatched: boolean;
}

const MemoryGame: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const shuffleArray = (array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = i % (shuffled.length - 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const initializeGame = useCallback(() => {
    const emojis = ['🎄', '🎁', '⭐', '❄️', '🎅', '🤶', '🍪', '🔔'];
    const doubledEmojis = [...emojis, ...emojis];
    const shuffledEmojis = shuffleArray(doubledEmojis);
    const shuffledCards = shuffledEmojis.map((emoji, index) => ({
      id: index,
      emoji,
      isFlipped: false,
      isMatched: false
    }));
    
    setCards(shuffledCards);
    setFlippedCards([]);
    setTimeLeft(60);
    setGameStarted(false);
    setGameWon(false);
    setShowConfetti(false);
  }, []);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  useEffect(() => {
    if (gameStarted && timeLeft > 0 && !gameWon) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, gameStarted, gameWon]);

  useEffect(() => {
    if (cards.length > 0 && cards.every(card => card.isMatched)) {
      setGameWon(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [cards]);

  const handleMatchedCards = (firstId: number, secondId: number) => {
    setCards(prev => prev.map(card => 
      card.id === firstId || card.id === secondId 
        ? { ...card, isMatched: true } 
        : card
    ));
    setFlippedCards([]);
  };

  const handleUnmatchedCards = (firstId: number, secondId: number) => {
    setCards(prev => prev.map(card => 
      card.id === firstId || card.id === secondId 
        ? { ...card, isFlipped: false } 
        : card
    ));
    setFlippedCards([]);
  };

  const processTwoFlippedCards = (firstId: number, secondId: number) => {
    const firstCard = cards[firstId];
    const secondCard = cards[secondId];

    if (firstCard.emoji === secondCard.emoji) {
      setTimeout(() => handleMatchedCards(firstId, secondId), 500);
    } else {
      setTimeout(() => handleUnmatchedCards(firstId, secondId), 1000);
    }
  };

  const handleCardClick = (cardId: number) => {
    if (!gameStarted) setGameStarted(true);
    
    if (flippedCards.length === 2 || cards[cardId].isFlipped || cards[cardId].isMatched) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    setCards(prev => prev.map(card => 
      card.id === cardId ? { ...card, isFlipped: true } : card
    ));

    if (newFlippedCards.length === 2) {
      const [firstId, secondId] = newFlippedCards;
      processTwoFlippedCards(firstId, secondId);
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      textAlign: 'center', 
      position: 'relative',
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url(/gameZoneImages/memoryGame/memory_back.jpg)',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      minHeight: '80vh',
      overflow: 'hidden'
    }}>
      <ConfettiAnimation show={showConfetti} />

      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0' }}>Spiel rechtzeitig abschließen und aufregende Preise gewinnen</h3>
        <p style={{ fontSize: '18px', fontWeight: 'bold', color: timeLeft <= 10 ? '#e74c3c' : '#191919' }}>
          ⏰ Zeit: {timeLeft}s
        </p>
        {gameWon && (
          <p style={{ color: '#4d943a', fontSize: '20px', fontWeight: 'bold' }}>
            🎉 Gewonnen! 🎉
          </p>
        )}
        {timeLeft === 0 && !gameWon && (
          <p style={{ color: '#e74c3c', fontSize: '18px' }}>
            ⏰ Zeit abgelaufen!
          </p>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px',
        maxWidth: '400px',
        margin: '0 auto 20px'
      }}>
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            disabled={timeLeft === 0 || gameWon}
            style={{
              width: '80px',
              height: '80px',
              fontSize: '30px',
              backgroundColor: card.isFlipped || card.isMatched ? '#fff' : '#BCB5A9',
              color: card.isFlipped || card.isMatched ? '#000' : '#fff',
              border: '2px solid #d4af37',
              borderRadius: '8px',
              cursor: timeLeft > 0 && !gameWon ? 'pointer' : 'default',
              transition: 'all 0.3s ease',
              opacity: card.isMatched ? 0.7 : 1
            }}
          >
            {card.isFlipped || card.isMatched ? card.emoji : '?'}
          </button>
        ))}
      </div>


    </div>
  );
};

export default MemoryGame;